import { YesObject } from '@yes.technology/react-toolkit'
import { FieldSettings } from 'shared/types'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import { RelationConfigProps } from 'site/Renderer/components/ObjectView/components/types/object-view'
import useReportData from '../useReportData/useReportData'
import useReportPdfs from '../useReportPdfs/useReportPdfs'
import useReportZip from '../useReportZip/useReportZip'
import useSplitReportComponents from '../useSplitReportComponents/useSplitReportComponents'

type UsePDFProcessingProps = {
  enabled: boolean
  reportType: 'list' | 'card'
  downloadType: 'single-pdf' | 'multiple-pdf-zip'
  splitThreshold: number
  yesObject?: YesObject
  layoutClassificationUuid?: string
  filteractionUuid?: string
  relationConfig?: RelationConfigProps
  fieldSettings?: FieldSettings
  concatFields?: ConcatFields
  classificationUuid?: string
}

const usePDFProcessing = ({
  enabled,
  reportType,
  downloadType,
  yesObject,
  layoutClassificationUuid,
  filteractionUuid,
  splitThreshold,
  relationConfig,
  fieldSettings,
  concatFields,
  classificationUuid
}: UsePDFProcessingProps) => {
  const {
    objectFields,
    fieldValues,
    filterComposition,
    filterFields,
    yesObjects,
    requestLoading,
    requestFetched,
    relationsTableLayouts,
    filteractionName,
    error: dataError,
    hasRequiredReportData
  } = useReportData({
    filteractionUuid,
    relationConfig,
    fieldSettings,
    classificationUuid,
    enabled,
    reportType,
    layoutClassificationUuid,
    yesObject,
    concatFields
  })

  const reportComponents = useSplitReportComponents({
    ...objectFields,
    fieldValues,
    filterComposition,
    filterFields,
    reportType,
    layoutClassificationUuid,
    splitThreshold,
    yesObjects,
    filteractionName,
    relationsTableLayouts,
    concatFields,
    relationConfig,
    fieldSettings,
    showHeaderForEveryChunk: downloadType === 'multiple-pdf-zip',
    isSinglePdfExport: downloadType === 'single-pdf' && splitThreshold === 1,
    enabled: enabled && (requestFetched || !!yesObject) && yesObjects.length > 0
  })

  const shouldMerge = downloadType !== 'multiple-pdf-zip'

  const pdfsEnabled = reportComponents.length > 0

  const {
    urls,
    blobs,
    isRendering,
    isRenderFinished,
    error: pdfError
  } = useReportPdfs({
    components: reportComponents,
    sourceObjects: yesObjects,
    shouldMerge,
    enabled: pdfsEnabled
  })

  const zipEnabled =
    enabled && downloadType === 'multiple-pdf-zip' && blobs.length > 0

  const {
    url: zipUrl,
    isPacking,
    isPackingFinished,
    error: zipError
  } = useReportZip({
    blobs,
    sourceObjects: yesObjects,
    enabled: zipEnabled
  })

  const currentError = dataError || pdfError || zipError

  const calculatedUrl = zipEnabled
    ? zipUrl
    : shouldMerge && urls.length > 0
      ? urls[0]
      : null

  const isLoading = requestLoading || isRendering || isPacking

  const allLoadsFinished =
    (requestFetched || !!yesObject) &&
    isRenderFinished &&
    (isPackingFinished || !zipEnabled) &&
    !isLoading

  return {
    currentError,
    calculatedUrl,
    isLoading,
    allLoadsFinished,
    hasRequiredReportData
  }
}

export default usePDFProcessing
